import { Dialog, Transition } from "@headlessui/react";
import { CheckIcon, XIcon } from "@heroicons/react/solid";
import React, { Fragment, useCallback, useMemo, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import AlertMessage from "../../../components/AlertMessage";
import LoadingIcon from "../../../components/icons/LoadingIcon";
import UpgradeSimulationsModal from "../../../components/modals/UpgradeSimulationsModal";
import { useBillingPlans } from "../../../context/billingPlans/BillingPlansProvider";
import { useLocations } from "../../../context/locations/LocationsProvider";
import { ModalConfig } from "../../../context/toast/ToastProvider";
import { useWorkspaces } from "../../../context/workspaces";
import { formatDateToLocale } from "../../../utils/dates";
import { HotspotLocation, PaymentStep, PlanType } from "../../../utils/types";
import useMapConfig from "../../../utils/useMapConfig";

interface Props {
  setErrorMessage: (message: string | null) => void;
  config: { locations: HotspotLocation[] } | undefined;
  onClose: () => void;
}

const RunSuperSimulationsModal: React.FC<Props> = ({ onClose, config }) => {
  const renderDistance = useMapConfig((state) => state.renderDistance);
  const { generateSuperSimulations } = useLocations();
  const [isLoading, setIsLoading] = useState(false);
  const { currentWorkspace } = useWorkspaces();
  const { currentPlan, setPaymentStep, setNewPlan } = useBillingPlans();
  const dialogRef = useRef(null);
  const [upgradeSimulationsConfig, setUpgradeSimulationsConfig] =
    useState<ModalConfig>({
      open: false
    });

  const locations = useMemo(() => config?.locations || [], [config]);

  const limitExceededAlertMessage = useMemo(() => {
    if (!currentWorkspace || !currentWorkspace.active_plan) return null;

    const { allowed_simulations, simulations_used_on_period } =
      currentWorkspace.active_plan;

    const availableSuperSimulations =
      allowed_simulations - simulations_used_on_period;

    if (currentPlan?.type === PlanType.community) {
      return (
        <AlertMessage
          className="mt-6"
          type="warning"
          title={
            <div className="flex flex-col">
              <span className="font-bold">{currentPlan?.name} Plan</span>

              {simulations_used_on_period === 0 ? (
                <span>
                  This workspace has{" "}
                  <b className="text-hotspotty-300">{allowed_simulations}</b>{" "}
                  super simulation per month.{" "}
                  {locations.length === 1 ? (
                    <span>You will use this one simulation credit.</span>
                  ) : (
                    <span>
                      You are trying to do{" "}
                      <b className="text-hotspotty-300">{locations.length}</b>{" "}
                      simulation{locations.length === 1 ? "" : "s"} but you
                      don't have enough simulation credits{" "}
                      <NavLink
                        className="font-bold text-hotspotty-300 hover:underline"
                        onClick={() => {
                          setPaymentStep(PaymentStep.plans);
                        }}
                        to="/pricing"
                      >
                        upgrade your plan
                      </NavLink>
                      .
                    </span>
                  )}
                </span>
              ) : (
                <span>
                  You have one free super simulation per month but it has
                  already been used. You can increase this limit by{" "}
                  <NavLink
                    className="font-bold text-hotspotty-300 hover:underline"
                    onClick={() => {
                      setPaymentStep(PaymentStep.plans);
                    }}
                    to="/pricing"
                  >
                    upgrading your plan
                  </NavLink>
                  . Alternatively you can wait until your simulation credit
                  resets next month.
                </span>
              )}
            </div>
          }
        />
      );
    }

    return (
      <AlertMessage
        className="mt-6"
        type="warning"
        title={
          <div className="flex flex-col">
            <span>
              Your workspace has{" "}
              <b className="text-hotspotty-300">{availableSuperSimulations}</b>{" "}
              out of <b className="text-hotspotty-300">{allowed_simulations}</b>{" "}
              super simulations left for this billing period.
            </span>

            {locations.length > availableSuperSimulations ? (
              <div className="flex flex-col mt-2">
                <span>
                  You can <b className="text-hotspotty-300">not</b> run{" "}
                  <b className="text-hotspotty-300">{locations.length}</b> super
                  simulation
                  {locations.length === 1 ? "" : "s"}. Here are your options:
                </span>
                <ul className="my-4 space-y-4">
                  {availableSuperSimulations > 0 && (
                    <li className="flex space-x-3">
                      <CheckIcon
                        className="flex-shrink-0 h-5 w-5 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        Just run{" "}
                        <b className="text-hotspotty-300">
                          {availableSuperSimulations}
                        </b>{" "}
                        super simulation
                        {availableSuperSimulations === 1 ? "" : "s"} by
                        selecting location
                        {availableSuperSimulations === 1 ? "" : "s"} and using
                        the action button
                      </span>
                    </li>
                  )}
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      Wait until your super simulations reset on{" "}
                      {formatDateToLocale(
                        currentWorkspace.active_plan.next_billing_date,
                        true
                      )}
                    </span>
                  </li>
                  <li className="flex space-x-3">
                    <CheckIcon
                      className="flex-shrink-0 h-5 w-5 text-green-500"
                      aria-hidden="true"
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-300">
                      Increase the monthly super simulations limit and proceed
                      immediately
                    </span>
                  </li>
                </ul>
                <span
                  className="mt-4 cursor-pointer inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500"
                  onClick={() => {
                    const minimumSimulations =
                      allowed_simulations +
                      (locations.length - availableSuperSimulations);

                    setNewPlan((prevState) => ({
                      ...prevState,
                      simulationAmount: minimumSimulations
                    }));

                    setUpgradeSimulationsConfig({ open: true });
                  }}
                >
                  Upgrade super simulations
                </span>
              </div>
            ) : (
              <span>
                You will use{" "}
                <b className="text-hotspotty-300">{locations.length}</b>{" "}
                simulation credit{locations.length === 1 ? "" : "s"}.
              </span>
            )}
          </div>
        }
      />
    );
  }, [
    currentWorkspace,
    currentPlan?.type,
    currentPlan?.name,
    locations.length,
    setPaymentStep,
    setNewPlan
  ]);

  const checkCanShow = useCallback(() => {
    if ((!!config && upgradeSimulationsConfig.open) || !!config) return true;

    return false;
  }, [upgradeSimulationsConfig.open, config]);

  return (
    <>
      <UpgradeSimulationsModal
        config={upgradeSimulationsConfig}
        setConfig={setUpgradeSimulationsConfig}
      />

      <Transition.Root show={checkCanShow()} as={Fragment}>
        <Dialog
          as="div"
          static
          className="fixed z-10 inset-0 overflow-y-auto"
          open={checkCanShow()}
          onClose={() => {
            if (upgradeSimulationsConfig.open) {
              return;
            }

            onClose();
          }}
          initialFocus={dialogRef}
        >
          <div
            ref={dialogRef}
            className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            {/* This element is to trick the browser into centering the modal contents. */}
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="inline-block align-bottom bg-white dark:bg-gray-900 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
                <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                  <button
                    type="button"
                    className="bg-white dark:bg-gray-800 rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <div className="mt-3 sm:mt-0">
                  <Dialog.Title
                    as="h3"
                    className="text-lg leading-6 font-medium text-gray-900 dark:text-white"
                  >
                    Run {locations.length} Super Simulation
                    {locations.length === 1 ? "" : "s"}
                  </Dialog.Title>

                  {limitExceededAlertMessage}

                  <p className="mt-4 dark:text-gray-300">
                    Consider the following when running super simulations:
                  </p>

                  <ul className="my-4 space-y-4">
                    <li className="flex space-x-3">
                      <CheckIcon
                        className="flex-shrink-0 h-5 w-5 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        A default height of {renderDistance(10, false)} is used,
                        unless you added a custom height to your locations
                      </span>
                    </li>
                    <li className="flex space-x-3">
                      <CheckIcon
                        className="flex-shrink-0 h-5 w-5 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        You will receive an email when the super simulations are
                        ready
                      </span>
                    </li>
                    <li className="flex space-x-3">
                      <CheckIcon
                        className="flex-shrink-0 h-5 w-5 text-green-500"
                        aria-hidden="true"
                      />
                      <span className="text-sm text-gray-500 dark:text-gray-300">
                        Once processed, locations can be prioritized by sorting
                        on the <b>PoC Score</b> column
                      </span>
                    </li>
                  </ul>
                </div>
                <div className="mt-5 sm:mt-4 sm:flex justify-end">
                  <div>
                    <button
                      type="button"
                      className="mt-3 sm:mt-0 w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 sm:text-sm dark:bg-gray-900 dark:text-gray-400 dark:hover:text-gray-300 dark:hover:bg-gray-800 dark:border-gray-700 dark:hover:border-gray-400"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="mt-3 sm:mt-0 sm:ml-4 w-full sm:w-auto inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 items-center disabled:opacity-50 disabled:cursor-default"
                      onClick={async () => {
                        setIsLoading(true);
                        await generateSuperSimulations(locations);
                        setIsLoading(false);
                        onClose();
                      }}
                      disabled={
                        isLoading ||
                        locations.length >
                          (currentWorkspace?.active_plan?.allowed_simulations ||
                            0) -
                            (currentWorkspace?.active_plan
                              ?.simulations_used_on_period || 0)
                      }
                    >
                      {isLoading && (
                        <LoadingIcon className="w-4 h-4 animate-spin -ml-1 mr-3" />
                      )}
                      <span>
                        {isLoading
                          ? "Started Processing"
                          : "Run Super Simulations"}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default RunSuperSimulationsModal;
